import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  Rating,
  Avatar,
  Stack
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  AccessTime,
  Language,
  Facebook,
  Instagram,
  Star,
  Business,
  Person
} from '@mui/icons-material';
import MessageButton from '../components/MessageButton';
import { API_BASE_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';

interface Review {
  id: number;
  rating: number;
  text: string;
  createdAt: string;
  userName: string;
}

interface Category {
  id: number;
  name: string;
}

interface ServiceProvider {
  id: number;
  businessName: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  description: string;
  services: string[];
  serviceArea: string;
  operatingHours: string;
  status: string;
  website?: string;
  facebook?: string;
  instagram?: string;
  photos: string[];
  averageRating: number;
  totalReviews: number;
  totalBookings: number;
  categories: Array<{
    category: Category;
  }>;
  reviews: Review[];
  claimedByUserId?: number;
}

const ProviderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [claiming, setClaiming] = useState(false);
  const [claimMessage, setClaimMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const fetchProviderDetails = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/providers/${id}`);
      if (!response.ok) {
        throw new Error('Provider not found');
      }
      const data = await response.json();
      setProvider(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchProviderDetails();
    }
  }, [id, fetchProviderDetails]);

  const handleBookService = () => {
    navigate('/bookings', { state: { providerId: provider?.id } });
  };

  const handleClaimProfile = async () => {
    if (!user?.id || !provider) return;
    
    setClaiming(true);
    setClaimMessage(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/providers/${provider.id}/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ userId: user.id })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to claim profile');
      }
      
      setClaimMessage({ type: 'success', text: 'Profile claimed successfully! You can now receive messages.' });
      // Refresh provider data
      fetchProviderDetails();
    } catch (err: any) {
      setClaimMessage({ type: 'error', text: err.message || 'Failed to claim profile' });
    } finally {
      setClaiming(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !provider) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error || 'Provider not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 6 }}>
        {/* Header Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
                  <Business fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h4" gutterBottom>
                    {provider.businessName}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip 
                      label={provider.status} 
                      color={provider.status === 'approved' ? 'success' : 'warning'}
                      size="small"
                    />
                    {provider.averageRating > 0 && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Rating value={provider.averageRating} precision={0.1} readOnly size="small" />
                        <Typography variant="body2" color="textSecondary">
                          ({provider.totalReviews} reviews)
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
              <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                {provider.description}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleBookService}
                  sx={{ mb: 2 }}
                >
                  Book Service
                </Button>
                {/* Provider messaging */}
                {provider.claimedByUserId ? (
                  <MessageButton
                    receiverId={provider.claimedByUserId}
                    receiverName={provider.businessName}
                    variant="outlined"
                    size="large"
                    startIcon
                  />
                ) : (
                  user && !provider.claimedByUserId && (
                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      onClick={handleClaimProfile}
                      disabled={claiming}
                      sx={{ mb: 2 }}
                    >
                      {claiming ? 'Claiming...' : 'Claim This Profile'}
                    </Button>
                  )
                )}
                {claimMessage && (
                  <Alert severity={claimMessage.type} sx={{ mt: 1 }}>
                    {claimMessage.text}
                  </Alert>
                )}
                <Box textAlign="center">
                  <Typography variant="h6" color="primary">
                    {provider.totalBookings}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Bookings Completed
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={4}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person color="primary" />
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Person color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Contact Person</Typography>
                      <Typography variant="body1">{provider.contactPerson}</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={2}>
                    <Phone color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Phone</Typography>
                      <Typography variant="body1">{provider.phone}</Typography>
                    </Box>
                  </Box>
                  
                  {provider.email && (
                    <Box display="flex" alignItems="center" gap={2}>
                      <Email color="action" />
                      <Box>
                        <Typography variant="body2" color="textSecondary">Email</Typography>
                        <Typography variant="body1">{provider.email}</Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Box display="flex" alignItems="center" gap={2}>
                    <LocationOn color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Address</Typography>
                      <Typography variant="body1">{provider.address}</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={2}>
                    <LocationOn color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Service Area</Typography>
                      <Typography variant="body1">{provider.serviceArea}</Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={2}>
                    <AccessTime color="action" />
                    <Box>
                      <Typography variant="body2" color="textSecondary">Operating Hours</Typography>
                      <Typography variant="body1">{provider.operatingHours}</Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Services & Categories */}
          <Grid item xs={12} md={6}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Services & Categories
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box mb={3}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Services Offered
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {provider.services.map((service, index) => (
                      <Chip 
                        key={index} 
                        label={service} 
                        variant="outlined" 
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Categories
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {provider.categories.map((cat) => (
                      <Chip 
                        key={cat.category.id} 
                        label={cat.category.name} 
                        variant="filled" 
                        size="small"
                        color="secondary"
                      />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Social Media & Website */}
          {(provider.website || provider.facebook || provider.instagram) && (
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Social Media & Website
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    {provider.website && (
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          startIcon={<Language />}
                          href={provider.website}
                          target="_blank"
                          fullWidth
                        >
                          Visit Website
                        </Button>
                      </Grid>
                    )}
                    {provider.facebook && (
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          startIcon={<Facebook />}
                          href={provider.facebook}
                          target="_blank"
                          fullWidth
                        >
                          Facebook Page
                        </Button>
                      </Grid>
                    )}
                    {provider.instagram && (
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="outlined"
                          startIcon={<Instagram />}
                          href={`https://instagram.com/${provider.instagram.replace('@', '')}`}
                          target="_blank"
                          fullWidth
                        >
                          Instagram
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Reviews Section */}
          {provider.reviews.length > 0 && (
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star color="primary" />
                    Reviews ({provider.totalReviews})
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    {provider.reviews.map((review) => (
                      <Grid item xs={12} key={review.id}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {review.userName}
                              </Typography>
                              <Rating value={review.rating} readOnly size="small" />
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {formatDate(review.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            {review.text}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default ProviderDetails; 